import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@clerk/clerk-react';
import MobileMenu from './MobileMenu';
import UserMenu from './UserMenu';
import SearchAutocomplete from '../search/SearchAutocomplete';
import { useAccessLevel } from '../../hooks/useAccessLevel';
import { useAuthModal } from '../auth/AuthModalContext';
import { ORBCON_REPORT_NAV_LABEL, ORBCON_REPORT_PATH } from '../../content/orbconReport';
import { readReopenSearch } from '../../utils/pageFrom';
import { useUserSidebar } from '../../hooks/useUserSidebar';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomepage = location.pathname === '/';
  const { canViewElearning, canViewData } = useAccessLevel();
  const { openAuthModal } = useAuthModal();
  const { compactChrome, compactNav } = useUserSidebar();
  const { isLoaded, isSignedIn } = useAuth();
  const showAccountMenu = Boolean(isLoaded && isSignedIn);
  const hideHeaderSearch = isHomepage && !showAccountMenu;

  useEffect(() => {
    if (!compactNav) setIsMobileMenuOpen(false);
  }, [compactNav]);

  const reopenSearch = readReopenSearch(location.state);

  useEffect(() => {
    if (reopenSearch) {
      setSearchOpen(true);
      return;
    }
    setSearchOpen(false);
  }, [location.pathname, location.key, reopenSearch]);

  useEffect(() => {
    if (!searchOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!searchRef.current?.contains(event.target as Node)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [searchOpen]);

  const navLinkClass = (...paths: string[]) => {
    const isActive = paths.some(
      (path) => location.pathname === path || location.pathname.startsWith(path + '/')
    );
    return `font-medium transition-colors py-9 border-b-2 ${
      isActive
        ? 'text-primary border-primary'
        : 'text-gray-700 hover:text-primary border-transparent'
    }`;
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img src="/images/ORBCoN-EN-Colour.png" alt="ORBCoN" className="h-20" />
            </Link>

            <nav className={`${compactNav ? 'hidden' : 'flex'} items-center space-x-8`}>
              <Link to="/resources" className={navLinkClass('/resources', '/toolkits', '/references')}>
                Resources
              </Link>
              <Link to={ORBCON_REPORT_PATH} className={navLinkClass(ORBCON_REPORT_PATH)}>
                {ORBCON_REPORT_NAV_LABEL}
              </Link>
              <Link to="/events" className={navLinkClass('/events')}>
                Events
              </Link>
              {canViewElearning && (
                <Link to="/elearning" className={navLinkClass('/elearning')}>
                  eLearning
                </Link>
              )}
              {canViewData && (
                <Link to="/data" className={navLinkClass('/data')}>
                  Data
                </Link>
              )}
            </nav>

            <div className="flex items-center space-x-2 sm:space-x-4 self-stretch">
              {!hideHeaderSearch && (
                <div ref={searchRef} className={`${compactChrome ? 'hidden' : 'flex'} relative self-stretch items-center`}>
                  <button
                    type="button"
                    aria-label="Search"
                    aria-expanded={searchOpen}
                    onClick={() => setSearchOpen((open) => !open)}
                    className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-gray-700 hover:text-primary focus-ring rounded-md"
                  >
                    <MagnifyingGlassIcon className="h-6 w-6" />
                  </button>
                  {searchOpen && (
                    <div className="absolute right-0 top-full mt-2 w-[28rem] max-w-[calc(100vw-1rem)] z-50">
                      <SearchAutocomplete
                        autoFocus
                        initialQuery={reopenSearch ?? ''}
                        onClose={() => setSearchOpen(false)}
                      />
                    </div>
                  )}
                </div>
              )}
              {showAccountMenu ? (
                <UserMenu />
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => openAuthModal('sign-in')}
                    className="min-h-[44px] px-3 text-gray-700 hover:text-primary font-medium focus-ring rounded-md"
                  >
                    Sign In
                  </button>
                  <button
                    type="button"
                    onClick={() => openAuthModal('sign-up')}
                    className="min-h-[44px] px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark font-medium transition-colors focus-ring"
                  >
                    Sign Up
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(true)}
                className={`${compactNav ? 'inline-flex' : 'hidden'} p-2 text-gray-700 hover:text-primary focus-ring rounded-md`}
                aria-label="Open menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>
  );
}
